import { Grid, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import { addLocationApi, addLocationsApi, getBusinessDataApi, getLocationListApi } from 'SupabseHandler';
import CircularProgress from '@mui/material/CircularProgress';


import {
    businessData as businessDataAtom,
    locationData as locationDataAtom,
    locationDataList,
    locationList as locationListAtom,
} from 'atoms';
import Button from 'common/Button';
import ButtonSecondary from 'common/ButtonSecondary';
import FormHeading from 'common/FormHeading';
import FormInputLabel from 'common/FormInputLabel';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import FormTextAreaInput from 'common/FormTextAreaInput';
import FormTextInput from 'common/FormTextInput';
import ButtonLoader from 'common/Loaders/ButtonLoader';
import StickyActionFooter from 'common/StickyActionFooter';
import { useRecoilState } from 'recoil';
import { styled } from 'styled-components';
import { useAuth } from 'context/AuthContext';

import React, { useEffect, useState } from 'react';

import LocationFormData from '../Location/LocationFormData';

const ContainerDiv = styled.div``;

const Div = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 16px;
    transition: all 0.3s ease;

    .text {
        font-family: Poppins, serif;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .imageContainer {
        border-radius: 8px;
        border: 1px solid #CBC1B7;
        background: ${(props) => props.theme.colorBackground};
        width: 100px;
        height: 100px;
        min-width: 100px;
        min-height: 100px;
        transition: all 0.3s ease;
        max-width: 100px;
        max-height: 100px;
        padding: 20px;
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        box-sizing: border-box;
        transition: border-color 0.3s ease;
        cursor: pointer;
        
        &:hover {
            border-color: #ff5f39;
        }
    }

    .imageText {
        color: ${(props) => props.theme.tertiaryFontColor};

        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    .addPhotoText {
        color: #999;
        text-align: center;
        transition: color 0.3s ease;
    }
    
    .imageContainer:hover .addPhotoText {
        color: #ff5f39;
    }
    
    .imageContainer:hover svg path,
    .imageContainer:hover svg circle {
        stroke: #ff5f39 !important;
        transition: stroke 0.3s ease;
    }

    .imageWrapper {
        position: relative;
        width: 100px;
        height: 100px;
        min-width: 100px;
        min-height: 100px;
        flex-shrink: 0;
        cursor: pointer;
        
        &:hover .image {
            border: 1px solid #ff5f39;
            opacity: 0.2;
        }
        
        &:hover .imageOverlay {
            display: flex;
        }
        
        &:hover .imageOverlay .addPhotoText {
            color: #ff5f39;
        }
        
        &:hover .imageOverlay svg path,
        &:hover .imageOverlay svg circle {
            stroke: #ff5f39 !important;
            transition: stroke 0.3s ease;
        }
    }

    .image {
        width: 100px;
        height: 100px;
        border-radius: 8px;
        flex-shrink: 0;
        border: 1px solid var(--Structural-Structure-3, #c4c4c4);
        transition: border-color 0.3s ease, border-width 0.3s ease, opacity 0.3s ease;
        display: block;
        object-fit: cover;
    }
    
    .imageOverlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: none;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 6px;
        pointer-events: none;
        border-radius: 8px;
    }
    
`;

const BusinessPopup = ({ open, onClose }) => {
    const { token, loading: authLoading } = useAuth();

    const [businessData, setBusinessData] = useRecoilState(businessDataAtom);
    const [formData, setFormData] = useRecoilState(locationDataList);
    const [locationList, setLocationList] = useRecoilState(locationListAtom);
    const [locationsCount, setLocationCount] = useState(1);
    const [imageUploading, setImageUploading] = useState(false);
    const [error, setError] = useState('');
    const [formBusinessData, setBusinessFormData] = useState({
        name: businessData?.name || '',
        business_url: businessData?.business_url || '',
        website_url: businessData?.website_url || '',
        image_url: businessData?.image_url || '',
        bio: businessData?.bio || '',
        email: businessData?.email || '',
    });
    const [initialBusinessFormState, setInitialBusinessFormState] = useState(null);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    
    // Function to validate individual fields and return error messages
    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'name':
                return !value?.trim() ? 'Business name is required' : '';
            case 'image_url':
                return !value ? 'Business logo is required' : '';
            case 'bio':
                return !value?.trim() ? 'Business bio is required' : '';
            case 'email':
                return !value?.trim() ? 'Contact email is required' : '';
            default:
                return '';
        }
    };

    // Function to validate all fields and set error state
    const validateAllFields = () => {
        const errors = {};
        const requiredFields = ['name', 'image_url', 'bio', 'email'];
        
        requiredFields.forEach(field => {
            const error = validateField(field, formBusinessData[field]);
            if (error) {
                errors[field] = error;
            }
        });
        
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Function to validate if all mandatory fields are filled
    const isFormValid = () => {
        // Check required business fields based on the 'required' props and handleSubmit logic
        const requiredBusinessFields = {
            name: !!formBusinessData.name?.trim(),
            image_url: !!formBusinessData.image_url,
            bio: !!formBusinessData.bio?.trim(),
            email: !!formBusinessData.email?.trim()
        };
        
        // Check if all business fields are valid
        const isBusinessDataValid = Object.values(requiredBusinessFields).every(field => field === true);
        
        // Location fields are now optional when creating a new business
        return isBusinessDataValid;
    };
    
    // Auto-fill business_url with hyphenated business name
    useEffect(() => {
        if (formBusinessData.name) {
            const hyphenatedName = formBusinessData.name.trim().replace(/\s+/g, '-').toLowerCase();
            setBusinessFormData(prev => ({
                ...prev,
                business_url: hyphenatedName
            }));
        }
    }, [formBusinessData.name]);
    
    useEffect(() => {
        if (open) {
            setInitialBusinessFormState(JSON.stringify(formBusinessData)); // Store initial form data
            
            // Extract business name from full URL (if present)
            let businessUrlValue = businessData?.business_url || '';
            const baseUrl = process.env.REACT_APP_PATIENT_PORTAL_URL+'/business/';
            
            // If the business_url contains the base URL, remove it to display only the name part
            if (businessUrlValue && businessUrlValue.includes(baseUrl)) {
                businessUrlValue = businessUrlValue.replace(baseUrl, '');
            }
            
            // Re-initialize form data when the popup opens
            setBusinessFormData({
                name: businessData?.name || '',
                business_url: businessUrlValue,
                website_url: businessData?.website_url || '',
                image_url: businessData?.image_url || '',
                bio: businessData?.bio || '',
                email: businessData?.email || '',
            });
            
            // The name useEffect will trigger if there's a name but no business_url
            if (businessData?.name && !businessData?.business_url) {
                const hyphenatedName = businessData.name.trim().replace(/\s+/g, '-').toLowerCase();
                setBusinessFormData(prev => ({
                    ...prev,
                    business_url: hyphenatedName
                }));
            }
        }
    }, [open, businessData]);

    const handleClose = () => {
        const hasUnsavedChanges = JSON.stringify(formBusinessData) !== initialBusinessFormState;

        if (hasUnsavedChanges) {
            const confirmExit = window.confirm('You have unsaved changes. Do you really want to close?');
            if (!confirmExit) return;
        }

        setIsSubmitting(false);
        onPopupClose();
    };

    const getLocationList = async (id) => {
        try {
            if (authLoading) {
                console.error('Authentication still loading');
                return;
            }
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            const data = await getLocationListApi(id, token);
            setLocationList(data);
        } catch (err) {}
    };

    const addLocation = async (id) => {
        try {
            if (authLoading) {
                console.error('Authentication still loading');
                return;
            }
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            await addLocationsApi(formData, id, token);
            await getLocationList(id);
        } catch (err) {
            console.error('Error adding location:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Trim website_url and email values to remove leading/trailing whitespace
        // Convert email to lowercase for consistency
        const processedValue = name === 'email' ? value.trim().toLowerCase() :
                              name === 'website_url' ? value.trim() : value;
        setBusinessFormData({ ...formBusinessData, [name]: processedValue });
        
        // Clear field error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // const isBuisnessAdded =
    //     businessData?.business_url?.length > 0 &&
    //     businessData?.email?.length > 0 &&
    //     businessData?.website_url?.length > 0;
    const isBuisnessAdded = businessData?.email?.length > 0;

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            setAttemptedSubmit(true);
            
            // Validate all fields and show specific error messages
            if (!validateAllFields()) {
                setError('Please complete all required fields before continuing.');
                return;
            }
            
            setIsSubmitting(true);
            
            
            // Prepare data for submission
            const businessUrlValue = formBusinessData.business_url;
            
            // Create a copy of the form data to submit
            const dataToSubmit = {
                ...formBusinessData,
                // Store only the custom part without the base URL
                business_url: businessUrlValue
            };

            // Wait for auth to finish loading
            if (authLoading) {
                setError('Please wait while authentication loads...');
                setIsSubmitting(false);
                return;
            }

            // Validate access token
            if (!token) {
                setError('No authentication token found. Please try logging in again.');
                setIsSubmitting(false);
                return;
            }

            // Determine if we're creating a new business or updating an existing one
            const isNewBusiness = !isBuisnessAdded;
            
            let apiPayload;
            let apiEndpoint;
            let method;
            
            if (isNewBusiness) {
                // For new business, use create endpoint (POST)
                // Don't include id - let the database generate it
                apiPayload = {
                    ...dataToSubmit,
                    is_approved: false,
                    status: 'pending'
                };
                // Include userid if available from businessData
                if (businessData?.userid) {
                    apiPayload.userid = businessData.userid;
                }
                apiEndpoint = `${process.env.REACT_APP_SERVER_URL}/api/providers/create-business`;
                method = 'POST';
            } else {
                // For existing business, use update endpoint (PUT)
                const mandatoryFields = ['name', 'image_url', 'bio', 'email'];
                const hasMandatoryChanges = mandatoryFields.some((field) => {
                    const currentValue = field === 'email'
                        ? (formBusinessData[field]?.trim()?.toLowerCase() || '')
                        : (formBusinessData[field] || '');
                    const originalValue = field === 'email'
                        ? (businessData?.[field]?.trim()?.toLowerCase() || '')
                        : (businessData?.[field] || '');
                    return currentValue !== originalValue;
                });
                
                apiPayload = {
                    id: businessData.id,
                    ...dataToSubmit
                };
                
                // Set status and is_approved if mandatory fields changed
                if (hasMandatoryChanges) {
                    apiPayload.is_approved = false;
                    apiPayload.status = 'pending';
                }
                
                apiEndpoint = `${process.env.REACT_APP_SERVER_URL}/api/providers/update-business`;
                method = 'PUT';
            }

            // Make API call to backend
            const response = await fetch(apiEndpoint, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(apiPayload)
            });

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || (isNewBusiness 
                    ? 'Failed to create business. Please try again.' 
                    : 'Failed to update business. Please try again.');
                setError(errorMessage);
                console.error(isNewBusiness ? 'Create failed:' : 'Update failed:', errorMessage);
                setIsSubmitting(false);
                return;
            }

            // Backend returns { success: true, data: data, message: '...' }
            if (!result.success || !result.data) {
                setError('Unexpected response from server. Please try again.');
                setIsSubmitting(false);
                return;
            }

            // Get the business ID from the response (for new businesses) or use existing ID
            const businessId = isNewBusiness 
                ? (result.data[0]?.id || result.data?.id || businessData?.id)
                : businessData.id;

            if (!businessId) {
                setError('Failed to get business ID from response. Please try again.');
                setIsSubmitting(false);
                return;
            }

            // Add location if this is a new business and location data is provided
            if (isNewBusiness && formData.length > 0 && formData[0]?.location_name?.trim()) {
                await addLocation(businessId);
            }

            // Refresh business data
            const businessApiData = await getBusinessDataApi(businessId, token);
            // Handle both object and array cases - preserve structure
            const businessDataToSet = Array.isArray(businessApiData) ? businessApiData[0] : businessApiData;
            setBusinessData(businessDataToSet);
            onClose();
        } catch (error) {
            console.error('Error in handleSubmit:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onPopupClose = () => {
        onClose();
        setLocationCount(1);
    };

    const handleDisabledButtonClick = () => {
        if (!isFormValid()) {
            setError('Please complete all required fields before continuing.');
            validateAllFields(); // Show field-specific errors
        }
    };
    const handleImageUpload = async (file) => {
        try {
          const formData = new FormData();
          formData.append('file', file);
      
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/api/providers/upload/business-image`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
              },
              body: formData,
            }
          );
      
          const result = await response.json();
      
          if (!response.ok || result.error) {
            throw new Error(result.error || 'Failed to upload image');
          }
      
          const fileUrl =
            result.data?.url ||
            result.data?.image_url ||
            result.url ||
            result.image_url;
      
          if (!fileUrl) {
            throw new Error('No image URL returned from server');
          }
      
          return fileUrl;
        } catch (err) {
          console.error('Upload error:', err);
          throw err;
        }
      };
      const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
      
        if (authLoading) {
          setError('Please wait while authentication loads...');
          return;
        }
      
        if (!token) {
          setError('Authentication required. Please login again.');
          return;
        }
      
        // Preview immediately
        const previewUrl = URL.createObjectURL(file);
      
        setBusinessFormData((prev) => ({
          ...prev,
          image_url: previewUrl,
        }));
      
        try {
          setImageUploading(true); // 🔥 START LOADER
      
          const uploadedUrl = await handleImageUpload(file);
      
          setBusinessFormData((prev) => ({
            ...prev,
            image_url: uploadedUrl,
          }));
        } catch (err) {
          setError(err.message || 'Image upload failed');
        } finally {
          setImageUploading(false); // 🔥 STOP LOADER
        }
      };
    return (
        <ContainerDiv>
            <FormModal
                open={open}
                onClose={handleClose}
                title={!businessData?.id || !isBuisnessAdded ? 'Create your Business' : 'Edit your Business Profile'}
                subText={!businessData?.id || !isBuisnessAdded 
                    ? 'Fill out the form below to set up your business profile and main office location information. This business profile will be shown on the patient app as your business-level profile. Once submitted, our team will review your profile before publishing it to the marketplace.'
                    : 'Update your business profile information below. This business profile is shown on the patient app as your business-level profile.'
                }
                stickyFooter={
                    <StickyActionFooter
                        onCancel={businessData && isBuisnessAdded ? onPopupClose : onClose}
                        onSave={handleSubmit}
                        cancelText="Cancel"
                        saveText={businessData && isBuisnessAdded ? 'Save' : 'Create & Submit for Approval'}
                        isLoading={isSubmitting}
                        disabled={!isFormValid()||imageUploading}
                    />
                }
            >
                {!isBuisnessAdded && <FormHeading>Business Profile</FormHeading>}
                
                {error && <div className={'text-red-500 mt-2'}>{error}</div>}
                <FormStyle onSubmit={handleSubmit} className="businessForm" style={{ paddingTop: isBuisnessAdded ? '0px' : '32px' }}>
                    <Grid container spacing={2}>
                        <FormTextInput
                            formData={formBusinessData}
                            label="Business Name"
                            name="name"
                            handleChange={handleChange}
                            placeholder="Enter your business name"
                            required={true}
                            labelSize={12}
                            textSize={12}
                            error={fieldErrors.name}
                        />

                        <Grid item xs={12}>
                            <FormInputLabel label="Business Bio" labelSize={12} required={true} />
                            <div className="help-text" style={{ marginTop: '4px', marginBottom: '8px', paddingTop: '4px' }}>
                                Give clients an overview about your business.
                            </div>
                            <FormTextAreaInput
                                formData={formBusinessData}
                                label=""
                                name="bio"
                                handleChange={handleChange}
                                placeholder="Ex: Valletta Orthopaedics is dedicated to providing the highest quality of orthopaedic care. In addition to tre..."
                                required={true}
                                labelSize={12}
                                textSize={12}
                                noLabel={true}
                                value={formBusinessData.bio}
                                error={fieldErrors.bio}
                                style={{ marginBottom: '16px' }}
                            />
                        </Grid>

                        <FormTextInput
                            formData={formBusinessData}
                            label="Contact Email"
                            name="email"
                            handleChange={handleChange}
                            placeholder="Enter your contact email"
                            required={true}
                            labelSize={12}
                            textSize={12}
                            error={fieldErrors.email}
                            style={{ marginTop: '0px' }}
                        />

                        <FormTextInput
                            formData={formBusinessData}
                            label="Business Logo"
                            name="image_url"
                            handleChange={handleChange}
                            placeholder="Enter provider's email"
                            required={true}
                            labelSize={12}
                            textSize={12}
                            isTextFeild={false}
                            error={fieldErrors.image_url}
                        />

                        {/* Hidden file input for image upload */}
                        <input
  type="file"
  id="business-image"
  onChange={handleFileChange}
  className="hidden"
/>
{imageUploading && <CircularProgress size={20} />}
                        <Grid item xs={12}>
                            <Div>
                                {formBusinessData.image_url ? (
                                    <div className="imageWrapper" onClick={() => {
                                        document.getElementById('business-image').click();
                                    }}>
                                        <img
                                            src={formBusinessData.image_url}
                                            alt="Delete"
                                            id={'business-image-preview'}
                                            className="image"
                                        />
                                        <div className="imageOverlay">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                style={{
                                                    minWidth: '24px',
                                                    minHeight: '24px',
                                                    maxWidth: '24px',
                                                    maxHeight: '24px',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <path
                                                    d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                                                    stroke="#999"
                                                    strokeWidth="2"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    style={{ transition: 'stroke 0.3s ease' }}
                                                />
                                                <circle
                                                    cx="12"
                                                    cy="13"
                                                    r="4"
                                                    stroke="#999"
                                                    strokeWidth="2"
                                                    fill="none"
                                                    style={{ transition: 'stroke 0.3s ease' }}
                                                />
                                            </svg>
                                            <span className="text addPhotoText" style={{ fontSize: '12px' }}>Replace</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="imageContainer" onClick={() => {
                                        document.getElementById('business-image').click();
                                    }}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            style={{
                                                minWidth: '24px',
                                                minHeight: '24px',
                                                maxWidth: '24px',
                                                maxHeight: '24px',
                                                flexShrink: 0
                                            }}
                                        >
                                            <path
                                                d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                                                stroke="#999"
                                                strokeWidth="2"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ transition: 'stroke 0.3s ease' }}
                                            />
                                            <circle
                                                cx="12"
                                                cy="13"
                                                r="4"
                                                stroke="#999"
                                                strokeWidth="2"
                                                fill="none"
                                                style={{ transition: 'stroke 0.3s ease' }}
                                            />
                                        </svg>
                                        <span className="text addPhotoText" style={{ fontSize: '12px' }}>Add Logo</span>
                                    </div>
                                )}

                                <div className="imageText">
                                    <div className="help-text">
                                        Add a professional logo for your business to display in search results and on
                                        your business profile.
                                    </div>
                                    <div className="help-text">
                                        Upload a 200x200 image, up to 4MB in size.
                                    </div>
                                </div>
                            </Div>
                        </Grid>

                        <Grid item xs={12}>
                            <FormInputLabel label="Profile URL" labelSize={12} required={true} />
                            <div className="help-text" style={{ marginTop: '4px', marginBottom: '8px', paddingTop: '4px' }}>
                                This is the unique link to your business profile that you can share with patients. You don't need to edit it—your business name is added automatically, but you can change it if needed.
                            </div>
                            <FormTextInput
                                formData={formBusinessData}
                                label=""
                                name="business_url"
                                handleChange={handleChange}
                                placeholder="Enter your profile URL"
                                required={false}
                                labelSize={12}
                                textSize={12}
                                type={'text'}
                                noLabel={true}
                                InputProps={{
                                    startAdornment: (
                                        <Box
                                            className={
                                                '-ml-3.5 p-3 text-right text-sm '
                                            }
                                            sx={{
                                                backgroundColor: 'rgba(203, 193, 183, 1)',
                                                color: 'rgba(87, 83, 78, 1)',
                                                fontWeight: '500',
                                                borderTopLeftRadius: '7px',
                                                borderBottomLeftRadius: '7px',
                                            }}
                                        >
                                            {process.env.REACT_APP_PATIENT_PORTAL_URL}/business/
                                        </Box>
                                    ),
                                }}
                            />
                        </Grid>

                        <FormTextInput
                            formData={formBusinessData}
                            label="Website URL"
                            name="website_url"
                            handleChange={handleChange}
                            placeholder="Ex. www.yourwebsite.com"
                            required={false}
                            labelSize={12}
                            textSize={12}
                        />
                    </Grid>

                    {!isBuisnessAdded && (
                        <div className="locationData" style={{ paddingTop: '50px', paddingBottom: '60px' }}>
                            <FormHeading className="mainOffice">Main Office Location</FormHeading>
                            <div style={{ paddingTop: '32px' }}>
                                <LocationFormData 
                                    locationsCount={locationsCount} 
                                    setLocationCount={setLocationCount} 
                                    isCreating={!isBuisnessAdded}
                                />{' '}
                            </div>
                        </div>
                    )}
                    
                </FormStyle>
            </FormModal>
        </ContainerDiv>
    );
};

export default BusinessPopup;
